/**
 * Client ของ Delivery/Dispatch (P2-9g, D-105) — ห้าเส้นตาม router จริงของ
 * เซิร์ฟเวอร์ (`modules/sales/router.py`) ที่ `docs/API_CONTRACT.md` หัวข้อ
 * "Packing & Delivery" ระบุไว้ตรงกันทุกเส้น: `POST /v1/packing-units/{id}/load`,
 * `POST /v1/deliveries`, `GET /v1/deliveries`, `GET /v1/deliveries/{id}`,
 * `POST /v1/deliveries/{id}/reverse` — ตรวจกับ router จริงแล้วก่อนเขียนไฟล์นี้
 * (ไม่มี discrepancy แบบที่ `WO-P2-M9-006` เจอ)
 *
 * **ฝาแฝดของ `lib/packing/client.ts` ไม่ใช่ผู้ใช้ของมัน** — `lib/packing/**` อยู่ใน
 * forbidden_scope ของ Work Order นี้ (invariant "COPY PackingScreen's SHAPE, NOT
 * ITS FILES") เส้น `GET /v1/packing-units` และ `POST /v1/packing-units/{id}/load`
 * จึงถูก implement ซ้ำที่นี่ — ทั้งสองเป็นครึ่งแรกของ flow ที่หน้าจอนี้ต้องขับ (โหลด
 * ขึ้นรถก่อนจึงส่งได้) แม้ทรัพยากรจะชื่อ "packing unit" เหมือนกันกับหน้าจอ Packing
 *
 * หลักการเดียวกับ client อื่นทุกตัวในโปรเจกต์นี้:
 * - ฟังก์ชันในไฟล์นี้ **ไม่ throw** ความล้มเหลวทุกแบบกลายเป็นสถานะที่เรียกดูได้
 * - payload ที่ผิดรูปคือ `invalid-response` ไม่ใช่ "ไม่มีข้อมูล"
 * - **ไม่มี fallback ที่แต่งข้อมูลขึ้นมาเอง** และ **ไม่เขียนอะไรลง storage เลย**
 * - ทุกคำขอเดินทางผ่าน `apiFetch` ตัวเดียวกับ client อื่นทุกตัว — header
 *   `Authorization: Bearer` ถูกเติมที่นั่นจุดเดียว **ไม่ใช่ที่นี่**
 *
 * ⚠️ ไม่ใช่ด่านความปลอดภัย เซิร์ฟเวอร์เป็นผู้ตัดสินทุก request เสมอ
 * (`references/authorization-rbac.md` ข้อ 5)
 */

import type {
  CreateDeliveryBody,
  Delivery,
  DeliveryListPage,
  DeliveryLot,
  DeliverySummary,
  PackingUnitListPage,
  PackingUnitSummary,
  ReverseDeliveryBody,
} from './types';
import { apiFetch } from '../auth/authorizedFetch';
import { buildApiUrl, getConfiguredApiBaseUrl } from '../apiBase';

export const PACKING_UNITS_PATH = '/v1/packing-units';
export const DELIVERIES_PATH = '/v1/deliveries';

/** `packing_unit_id` เป็น UUID surrogate (D-039) — encode ไว้กันค่าที่มาจาก
 * payload ของเซิร์ฟเวอร์เปลี่ยนเส้นทางทั้งเส้นถ้าวันหนึ่งมันไม่ใช่สิ่งที่คิดไว้ */
export function packingUnitLoadPath(packingUnitId: string): string {
  return `${PACKING_UNITS_PATH}/${encodeURIComponent(packingUnitId)}/load`;
}

/** `delivery_id` เป็น UUID surrogate (D-039) เช่นกัน */
export function deliveryPath(deliveryId: string): string {
  return `${DELIVERIES_PATH}/${encodeURIComponent(deliveryId)}`;
}

export function deliveryReversePath(deliveryId: string): string {
  return `${deliveryPath(deliveryId)}/reverse`;
}

/** พารามิเตอร์ของ `GET /v1/packing-units` ที่ router รับจริง */
export interface PackingUnitListQuery {
  readonly plant_code?: string;
  readonly so_number?: string;
  readonly status?: string;
  readonly limit?: number;
  readonly cursor?: string;
}

/** พารามิเตอร์ของ `GET /v1/deliveries` ที่ router รับจริง — ไม่มี `status`
 * (delivery ไม่มีสถานะ ดูหมายเหตุใน `types.ts`) แต่มี `customer_code` แทน */
export interface DeliveryListQuery {
  readonly plant_code?: string;
  readonly so_number?: string;
  readonly customer_code?: string;
  readonly limit?: number;
  readonly cursor?: string;
}

function appendTrimmed(params: URLSearchParams, key: string, value: string | undefined): void {
  const trimmed = value?.trim() ?? '';
  if (trimmed !== '') {
    params.append(key, trimmed);
  }
}

export function buildPackingUnitListQuery(query: PackingUnitListQuery = {}): string {
  const params = new URLSearchParams();
  appendTrimmed(params, 'plant_code', query.plant_code);
  appendTrimmed(params, 'so_number', query.so_number);
  appendTrimmed(params, 'status', query.status);
  if (query.limit !== undefined) {
    params.append('limit', String(query.limit));
  }
  appendTrimmed(params, 'cursor', query.cursor);
  const serialized = params.toString();
  return serialized === '' ? '' : `?${serialized}`;
}

export function buildDeliveryListQuery(query: DeliveryListQuery = {}): string {
  const params = new URLSearchParams();
  appendTrimmed(params, 'plant_code', query.plant_code);
  appendTrimmed(params, 'so_number', query.so_number);
  appendTrimmed(params, 'customer_code', query.customer_code);
  if (query.limit !== undefined) {
    params.append('limit', String(query.limit));
  }
  appendTrimmed(params, 'cursor', query.cursor);
  const serialized = params.toString();
  return serialized === '' ? '' : `?${serialized}`;
}

/** Envelope ของ error ตาม `api/errors.py` (`error_code`, `message`, `details`) */
export interface ApiErrorEnvelope {
  readonly error_code: string | null;
  readonly message: string | null;
  /** `details.rule` — วิธีที่ contract ใช้แยกกรณีทางธุรกิจโดยไม่เพิ่ม top-level
   * error code ใหม่ */
  readonly rule: string | null;
  readonly details: Readonly<Record<string, unknown>>;
}

const EMPTY_ENVELOPE: ApiErrorEnvelope = {
  error_code: null,
  message: null,
  rule: null,
  details: {},
};

/** ความล้มเหลวทุกแบบที่ห้าเส้นนี้ตอบจริง แยกเป็นสถานะของตัวเอง — โครงเดียวกับ
 * client อื่นทุกตัวในโปรเจกต์นี้ */
export type DeliveryFailure =
  | { readonly status: 'unauthenticated' }
  | { readonly status: 'forbidden'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'not-found'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'conflict'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'rule-violation'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'bad-request'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'server-error'; readonly httpStatus: number }
  | { readonly status: 'network-error'; readonly detail: string }
  | { readonly status: 'invalid-response' }
  | { readonly status: 'cancelled' };

export type PackingUnitListResult =
  | { readonly status: 'ready'; readonly page: PackingUnitListPage }
  | DeliveryFailure;

export type PackingUnitActionResult =
  | { readonly status: 'ready'; readonly packingUnit: PackingUnitSummary }
  | DeliveryFailure;

export type DeliveryListResult =
  | { readonly status: 'ready'; readonly page: DeliveryListPage }
  | DeliveryFailure;

export type DeliveryDetailResult =
  | { readonly status: 'ready'; readonly delivery: Delivery }
  | DeliveryFailure;

export interface DeliveryRequestOptions {
  readonly apiBaseUrl?: string;
  readonly signal?: AbortSignal;
  readonly fetchImpl?: typeof fetch;
}

/* -------------------------------------------------------------- parsing -- */

function requiredString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

function requiredInteger(value: unknown): number | null {
  return typeof value === 'number' && Number.isInteger(value) ? value : null;
}

/** `undefined` แปลว่า "ผิดรูป" — คีย์ที่หายไปทั้งคีย์หรือมาเป็นชนิดผิด ถือว่าผิดรูป
 * เท่ากัน ส่วน `null` (ตามที่ `Optional[...] = None` ของ Pydantic serialize ออกมา)
 * เป็นค่าที่ถูกต้อง */
function nullableString(value: unknown): string | null | undefined {
  if (value === null || value === undefined) {
    return null;
  }
  return typeof value === 'string' ? value : undefined;
}

function parseCursor(raw: unknown): string | null | undefined {
  const cursor = nullableString(raw);
  if (cursor === undefined) {
    return undefined;
  }
  return cursor === null || cursor.trim() === '' ? null : cursor;
}

/**
 * กล่องหนึ่งใบ **ไม่มี lot** — ตรงตาม `PackingUnitListItemResponse` ใน
 * `schemas.py` ทุกฟิลด์ (เก้าฟิลด์แรกและ `packed_qty`/`uom_code`/`status`/
 * `created_by_employee_id`/`created_at`/`updated_at` required, สี่ฟิลด์
 * pack/unpack record nullable)
 */
export function parsePackingUnitSummary(raw: unknown): PackingUnitSummary | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;

  const packingUnitId = requiredString(record.packing_unit_id);
  const packageNo = requiredString(record.package_no);
  const plantId = requiredString(record.plant_id);
  const plantCode = requiredString(record.plant_code);
  const soId = requiredString(record.so_id);
  const soNumber = requiredString(record.so_number);
  const lineNo = requiredInteger(record.line_no);
  const productId = requiredString(record.product_id);
  const productCode = requiredString(record.product_code);
  const packingStandardId = requiredString(record.packing_standard_id);
  const storageLocationId = requiredString(record.storage_location_id);
  const storageLocationCode = requiredString(record.storage_location_code);
  const packedQty = requiredString(record.packed_qty);
  const uomCode = requiredString(record.uom_code);
  const status = requiredString(record.status);
  const createdByEmployeeId = requiredString(record.created_by_employee_id);
  const createdAt = requiredString(record.created_at);
  const updatedAt = requiredString(record.updated_at);

  if (
    packingUnitId === null ||
    packageNo === null ||
    plantId === null ||
    plantCode === null ||
    soId === null ||
    soNumber === null ||
    lineNo === null ||
    productId === null ||
    productCode === null ||
    packingStandardId === null ||
    storageLocationId === null ||
    storageLocationCode === null ||
    packedQty === null ||
    uomCode === null ||
    status === null ||
    createdByEmployeeId === null ||
    createdAt === null ||
    updatedAt === null
  ) {
    return null;
  }

  const packedByEmployeeId = nullableString(record.packed_by_employee_id);
  const packedAt = nullableString(record.packed_at);
  const unpackedByEmployeeId = nullableString(record.unpacked_by_employee_id);
  const unpackedAt = nullableString(record.unpacked_at);

  if (
    packedByEmployeeId === undefined ||
    packedAt === undefined ||
    unpackedByEmployeeId === undefined ||
    unpackedAt === undefined
  ) {
    return null;
  }

  return {
    packing_unit_id: packingUnitId,
    package_no: packageNo,
    plant_id: plantId,
    plant_code: plantCode,
    so_id: soId,
    so_number: soNumber,
    line_no: lineNo,
    product_id: productId,
    product_code: productCode,
    packing_standard_id: packingStandardId,
    storage_location_id: storageLocationId,
    storage_location_code: storageLocationCode,
    packed_qty: packedQty,
    uom_code: uomCode,
    status,
    created_by_employee_id: createdByEmployeeId,
    packed_by_employee_id: packedByEmployeeId,
    packed_at: packedAt,
    unpacked_by_employee_id: unpackedByEmployeeId,
    unpacked_at: unpackedAt,
    created_at: createdAt,
    updated_at: updatedAt,
  };
}

export function parsePackingUnitListPage(raw: unknown): PackingUnitListPage | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;
  if (!Array.isArray(record.items)) {
    return null;
  }

  const items: PackingUnitSummary[] = [];
  for (const entry of record.items) {
    const parsed = parsePackingUnitSummary(entry);
    if (parsed === null) {
      return null;
    }
    items.push(parsed);
  }

  const cursor = parseCursor(record.next_cursor);
  return cursor === undefined ? null : { items, next_cursor: cursor };
}

/** หนึ่ง lot ที่ออกไปในเดลิเวอรีนี้ — `DeliveryLotResponse` มีแค่สองฟิลด์ ทั้งคู่
 * required */
export function parseDeliveryLot(raw: unknown): DeliveryLot | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;
  const lotId = requiredString(record.lot_id);
  const shippedQty = requiredString(record.shipped_qty);
  if (lotId === null || shippedQty === null) {
    return null;
  }
  return { lot_id: lotId, shipped_qty: shippedQty };
}

/**
 * เดลิเวอรีหนึ่งใบ **ไม่รวม `lots`** — ตรงตาม `DeliveryListItemResponse`/
 * `DeliveryResponse` ใน `schemas.py` ทุกฟิลด์ — สามฟิลด์การกลับรายการ nullable
 * ร่วมกันเสมอ (`ck_delivery_reversal_record_is_whole`) ที่เหลือ required
 */
export function parseDeliverySummary(raw: unknown): DeliverySummary | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;

  const deliveryId = requiredString(record.delivery_id);
  const deliveryNo = requiredString(record.delivery_no);
  const plantId = requiredString(record.plant_id);
  const plantCode = requiredString(record.plant_code);
  const packingUnitId = requiredString(record.packing_unit_id);
  const packageNo = requiredString(record.package_no);
  const soId = requiredString(record.so_id);
  const soNumber = requiredString(record.so_number);
  const lineNo = requiredInteger(record.line_no);
  const customerId = requiredString(record.customer_id);
  const customerCode = requiredString(record.customer_code);
  const productId = requiredString(record.product_id);
  const productCode = requiredString(record.product_code);
  const storageLocationId = requiredString(record.storage_location_id);
  const storageLocationCode = requiredString(record.storage_location_code);
  const shippedQty = requiredString(record.shipped_qty);
  const uomCode = requiredString(record.uom_code);
  const packingUnitStatus = requiredString(record.packing_unit_status);
  const dispatchedByEmployeeId = requiredString(record.dispatched_by_employee_id);
  const dispatchedAt = requiredString(record.dispatched_at);
  const createdAt = requiredString(record.created_at);
  const updatedAt = requiredString(record.updated_at);

  if (
    deliveryId === null ||
    deliveryNo === null ||
    plantId === null ||
    plantCode === null ||
    packingUnitId === null ||
    packageNo === null ||
    soId === null ||
    soNumber === null ||
    lineNo === null ||
    customerId === null ||
    customerCode === null ||
    productId === null ||
    productCode === null ||
    storageLocationId === null ||
    storageLocationCode === null ||
    shippedQty === null ||
    uomCode === null ||
    packingUnitStatus === null ||
    dispatchedByEmployeeId === null ||
    dispatchedAt === null ||
    createdAt === null ||
    updatedAt === null
  ) {
    return null;
  }

  const reversedAt = nullableString(record.reversed_at);
  const reversedByEmployeeId = nullableString(record.reversed_by_employee_id);
  const reversalReason = nullableString(record.reversal_reason);

  if (reversedAt === undefined || reversedByEmployeeId === undefined || reversalReason === undefined) {
    return null;
  }
  // ``ck_delivery_reversal_record_is_whole`` — สามคอลัมน์นี้ต้อง NULL พร้อมกันหรือ
  // ไม่ก็ครบทั้งสาม แถวที่ไม่ตรงกฎนี้คือ payload ผิดรูป ไม่ใช่เดลิเวอรีที่กลับ
  // รายการครึ่งเดียว
  const reversalFieldCount = [reversedAt, reversedByEmployeeId, reversalReason].filter(
    (value) => value !== null,
  ).length;
  if (reversalFieldCount !== 0 && reversalFieldCount !== 3) {
    return null;
  }

  return {
    delivery_id: deliveryId,
    delivery_no: deliveryNo,
    plant_id: plantId,
    plant_code: plantCode,
    packing_unit_id: packingUnitId,
    package_no: packageNo,
    so_id: soId,
    so_number: soNumber,
    line_no: lineNo,
    customer_id: customerId,
    customer_code: customerCode,
    product_id: productId,
    product_code: productCode,
    storage_location_id: storageLocationId,
    storage_location_code: storageLocationCode,
    shipped_qty: shippedQty,
    uom_code: uomCode,
    packing_unit_status: packingUnitStatus,
    dispatched_by_employee_id: dispatchedByEmployeeId,
    dispatched_at: dispatchedAt,
    reversed_at: reversedAt,
    reversed_by_employee_id: reversedByEmployeeId,
    reversal_reason: reversalReason,
    created_at: createdAt,
    updated_at: updatedAt,
  };
}

/** เดลิเวอรีเต็มใบ **พร้อม lot ครบ** — `lots` แถวเดียวที่อ่านไม่ออกทำให้ทั้งเดลิเวอรี
 * ผิดรูป ไม่ใช่ถูกตัดทิ้งเงียบ ๆ: lot ที่หายไปคือของที่ customer lot traceability
 * จะตอบไม่ได้ */
export function parseDelivery(raw: unknown): Delivery | null {
  const summary = parseDeliverySummary(raw);
  if (summary === null) {
    return null;
  }
  const record = raw as Record<string, unknown>;
  if (!Array.isArray(record.lots)) {
    return null;
  }

  const lots: DeliveryLot[] = [];
  for (const entry of record.lots) {
    const lot = parseDeliveryLot(entry);
    if (lot === null) {
      return null;
    }
    lots.push(lot);
  }

  return { ...summary, lots };
}

export function parseDeliveryListPage(raw: unknown): DeliveryListPage | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;
  if (!Array.isArray(record.items)) {
    return null;
  }

  const items: DeliverySummary[] = [];
  for (const entry of record.items) {
    const parsed = parseDeliverySummary(entry);
    if (parsed === null) {
      return null;
    }
    items.push(parsed);
  }

  const cursor = parseCursor(record.next_cursor);
  return cursor === undefined ? null : { items, next_cursor: cursor };
}

function parseErrorEnvelope(raw: unknown): ApiErrorEnvelope {
  if (typeof raw !== 'object' || raw === null) {
    return EMPTY_ENVELOPE;
  }
  const record = raw as Record<string, unknown>;
  const details =
    typeof record.details === 'object' && record.details !== null
      ? (record.details as Record<string, unknown>)
      : {};

  return {
    error_code: requiredString(record.error_code),
    message: requiredString(record.message),
    rule: requiredString(details.rule),
    details,
  };
}

/* ------------------------------------------------------------- requests -- */

async function readEnvelope(response: Response): Promise<ApiErrorEnvelope> {
  try {
    return parseErrorEnvelope(await response.json());
  } catch {
    return EMPTY_ENVELOPE;
  }
}

type PayloadResult =
  | { readonly status: 'payload'; readonly payload: unknown }
  | DeliveryFailure;

async function requestPayload(
  path: string,
  init: RequestInit,
  options: DeliveryRequestOptions,
): Promise<PayloadResult> {
  const fetchImpl = options.fetchImpl ?? apiFetch;
  if (typeof fetchImpl !== 'function') {
    return { status: 'network-error', detail: 'fetch ไม่พร้อมใช้งานใน runtime นี้' };
  }

  const baseUrl = options.apiBaseUrl ?? getConfiguredApiBaseUrl();
  const url = buildApiUrl(baseUrl, path);

  let response: Response;
  try {
    response = await fetchImpl(url, {
      credentials: 'same-origin',
      cache: 'no-store',
      ...init,
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch (error) {
    if (options.signal?.aborted || (error as Error | undefined)?.name === 'AbortError') {
      return { status: 'cancelled' };
    }
    return {
      status: 'network-error',
      detail: error instanceof Error ? error.message : 'ไม่ทราบสาเหตุ',
    };
  }

  if (options.signal?.aborted) {
    return { status: 'cancelled' };
  }

  switch (response.status) {
    case 400:
      return { status: 'bad-request', error: await readEnvelope(response) };
    case 401:
      return { status: 'unauthenticated' };
    case 403:
      return { status: 'forbidden', error: await readEnvelope(response) };
    case 404:
      return { status: 'not-found', error: await readEnvelope(response) };
    case 409:
      return { status: 'conflict', error: await readEnvelope(response) };
    case 422:
      return { status: 'rule-violation', error: await readEnvelope(response) };
    default:
      break;
  }

  if (!response.ok) {
    return { status: 'server-error', httpStatus: response.status };
  }

  try {
    return { status: 'payload', payload: await response.json() };
  } catch {
    return { status: 'invalid-response' };
  }
}

/**
 * `GET /v1/packing-units` — กรองตามขอบเขตโรงงานของผู้เรียกเสมอ, ไม่มี `lots`
 *
 * หน้าจอนี้ใช้เส้นนี้เพื่อหากล่องที่ `PACKED` (พร้อมโหลดขึ้นรถ) และ `LOADED`
 * (พร้อมส่ง) — **ไม่เรียงลำดับซ้ำเอง** เรียงตาม `package_no` ที่เซิร์ฟเวอร์ส่งมา
 */
export async function fetchPackingUnits(
  query: PackingUnitListQuery = {},
  options: DeliveryRequestOptions = {},
): Promise<PackingUnitListResult> {
  const result = await requestPayload(
    `${PACKING_UNITS_PATH}${buildPackingUnitListQuery(query)}`,
    { method: 'GET', headers: { Accept: 'application/json' } },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }

  const page = parsePackingUnitListPage(result.payload);
  return page === null ? { status: 'invalid-response' } : { status: 'ready', page };
}

/**
 * `POST /v1/packing-units/{id}/load` — `PACKED → LOADED` (EDIT), **ไม่ขยับสต๊อก**
 *
 * body ว่างเปล่าเสมอ เหมือน transition ของ Packing — `expected_status` (optional)
 * ไม่ถูกส่ง เพราะการเปิด staleness guard เพิ่มไม่ใช่สิ่งที่ Work Order นี้ต้องพิสูจน์
 * และคีย์ที่ไม่ได้ประกาศไว้ใน `extra="forbid"` จะตอบ `400`
 */
export async function loadPackingUnit(
  packingUnitId: string,
  options: DeliveryRequestOptions = {},
): Promise<PackingUnitActionResult> {
  const result = await requestPayload(
    packingUnitLoadPath(packingUnitId),
    {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }
  const packingUnit = parsePackingUnitSummary(result.payload);
  return packingUnit === null ? { status: 'invalid-response' } : { status: 'ready', packingUnit };
}

/**
 * `POST /v1/deliveries` — `LOADED → DISPATCHED`, สแกนกล่องออกจากประตู (CREATE)
 *
 * **นี่คือจุดเดียวในทั้งเวอร์ติคัลที่สต๊อกออกจากบริษัทจริง** ตอบ `201` สำหรับการ
 * ส่งที่เกิดขึ้นจริง และ `200` สำหรับการสแกนซ้ำของกล่องที่ส่งไปแล้ว (D-101 replay) —
 * ทั้งสองกรณีเป็น `status: 'ready'` เหมือนกันที่นี่ หน้าจอไม่ต้องแยกพฤติกรรม เพราะ
 * ทั้งสองแปลว่า "กล่องนี้ถูกส่งแล้วจริง"
 */
export async function createDelivery(
  body: CreateDeliveryBody,
  options: DeliveryRequestOptions = {},
): Promise<DeliveryDetailResult> {
  const result = await requestPayload(
    DELIVERIES_PATH,
    {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({ package_no: body.package_no }),
    },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }
  const delivery = parseDelivery(result.payload);
  return delivery === null ? { status: 'invalid-response' } : { status: 'ready', delivery };
}

/** `GET /v1/deliveries` — กรองตามขอบเขตโรงงานของผู้เรียกเสมอ, ไม่มี `lots` —
 * รวมเดลิเวอรีที่ถูกกลับรายการแล้วด้วยเสมอ ไม่มีการกรองออก */
export async function fetchDeliveries(
  query: DeliveryListQuery = {},
  options: DeliveryRequestOptions = {},
): Promise<DeliveryListResult> {
  const result = await requestPayload(
    `${DELIVERIES_PATH}${buildDeliveryListQuery(query)}`,
    { method: 'GET', headers: { Accept: 'application/json' } },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }

  const page = parseDeliveryListPage(result.payload);
  return page === null ? { status: 'invalid-response' } : { status: 'ready', page };
}

/** `GET /v1/deliveries/{id}` — เดลิเวอรีเดียว **พร้อม lot ครบ** เพื่อตามสอบ lot ของ
 * ลูกค้า (คำนวณสด ไม่เก็บ — D-095/D-099) */
export async function fetchDelivery(
  deliveryId: string,
  options: DeliveryRequestOptions = {},
): Promise<DeliveryDetailResult> {
  const result = await requestPayload(
    deliveryPath(deliveryId),
    { method: 'GET', headers: { Accept: 'application/json' } },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }
  const delivery = parseDelivery(result.payload);
  return delivery === null ? { status: 'invalid-response' } : { status: 'ready', delivery };
}

/**
 * `POST /v1/deliveries/{id}/reverse` — `DISPATCHED → RETURNED`, ถอนการขาย
 * (APPROVE) — **ไม่มี replay** ส่งซ้ำตอบ `422 DELIVERY_ALREADY_REVERSED` เสมอ
 *
 * `reason` เป็นฟิลด์เดียวและ **ต้องมี** — ผู้เรียกฟังก์ชันนี้ต้องกรองสตริงว่างออกเอง
 * ก่อนเรียก (ปุ่มบนหน้าจอถูกปิดไว้จนกว่าจะกรอก ดู `deliveryView.ts`)
 */
export async function reverseDelivery(
  deliveryId: string,
  body: ReverseDeliveryBody,
  options: DeliveryRequestOptions = {},
): Promise<DeliveryDetailResult> {
  const result = await requestPayload(
    deliveryReversePath(deliveryId),
    {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason: body.reason }),
    },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }
  const delivery = parseDelivery(result.payload);
  return delivery === null ? { status: 'invalid-response' } : { status: 'ready', delivery };
}

/** อ่าน `details.rule` จากความล้มเหลวที่มี envelope — คืน `null` เมื่อไม่มี */
export function ruleFromFailure(failure: DeliveryFailure): string | null {
  return 'error' in failure ? failure.error.rule : null;
}

/** อ่าน `details.current_status` — สถานะที่กล่องนั้นถืออยู่จริงตอน `409` ที่เกี่ยว
 * กับ transition ของกล่อง (`load`/`dispatch`) */
export function currentStatusFromFailure(failure: DeliveryFailure): string | null {
  if (!('error' in failure)) {
    return null;
  }
  const value = failure.error.details.current_status;
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}
